import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ClientSearch } from './ClientSearch.tsx';
import { MainMenu } from './MainMenu.tsx';
import { QuickSearchProvider } from './QuickSearch.tsx';
import { useMe } from '../api/queries.ts';

/**
 * The application shell: the wordmark, finding a client, and the menu, above
 * every signed-in screen.
 *
 * **Ctrl+F (⌘F) is the quick search on every screen under it**, not the
 * browser's find bar: see QuickSearch.tsx. A list added later gets the search
 * by being here, and can let it jump to rows by marking them.
 *
 * **The bar scrolls away; it is not sticky.** A client's page pins its Critical
 * band to the top of the screen, full-bleed, above everything. A sticky bar
 * would sit over it, and that band is the one thing on the highest-risk screen
 * that cannot be argued about.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const me = useMe();
  return (
    <QuickSearchProvider>
      {me.data?.breakGlass && <BreakGlassBand />}
      <header className="border-b border-neutral-200 bg-white">
        <div className="mx-auto flex max-w-[96rem] flex-wrap items-center gap-x-6 gap-y-2 px-4 py-2 lg:flex-nowrap lg:px-6">
          <Wordmark />
          <ClientSearch className="order-last w-full lg:order-none lg:w-auto lg:max-w-xl lg:flex-1" />
          <div className="ml-auto">
            <MainMenu />
          </div>
        </div>
      </header>
      {children}
    </QuickSearchProvider>
  );
}

/**
 * "procura", lowercase, in Comfortaa 300, with "for Allied CSS" in Questrial.
 * The two faces are for this and nothing else: see .wordmark-* in index.css.
 */
function Wordmark() {
  return (
    <Link
      to="/"
      aria-label="procura for Allied CSS, home"
      className="inline-flex min-h-[44px] shrink-0 items-baseline gap-2 text-neutral-900"
    >
      <span className="wordmark-procura text-[2rem] leading-none">procura</span>
      <span className="wordmark-sub text-base leading-none text-neutral-600">for Allied CSS</span>
    </Link>
  );
}

/**
 * On every screen while the break-glass account is signed in (062).
 *
 * The person using it must not be able to forget it: every page they open and
 * every change they make is recorded against a break-glass session, and the
 * session's start has been reported to Arthur. Words, not only colour. It
 * scrolls away with the header rather than sticking, so it never sits over a
 * client's Critical band.
 */
function BreakGlassBand() {
  return (
    <div role="status" className="bg-rose-800 px-4 py-2 text-center text-[0.9375rem] font-semibold text-white">
      Break-glass account. Every page you open and every change you make is recorded, and your use has
      been reported. Sign out when the emergency is over.
    </div>
  );
}
